import { Component, Input, Output, EventEmitter } from "@angular/core";
import { SpartaxFormStepBaseComponent } from "../spartax-form-step-base";
import { SpartaxCQBaseDataDto, SpartaxCQQuestionType } from "src/app/data/dto/spartax/spartax-cq.dto";
import { from } from "linq-to-typescript";
import * as moment from "moment";

@Component({
    selector: 'app-spartax-cq-master',
    templateUrl: './spartax-cq-master.component.html',
    styleUrls: ['./spartax-cq-master.component.scss'],
})
export class SpartaxCQMasterComponent extends SpartaxFormStepBaseComponent {
    @Input()
    baseData: SpartaxCQBaseDataDto;

    @Input()
    partnerEventsLinkUrl: string;

    @Output()
    navigateToPreviousStepper = new EventEmitter<void>();

    @Output()
    navigateToNextStepper = new EventEmitter<void>();

    public SpartaxCQQuestionType = SpartaxCQQuestionType;
    public activeSectionIndex = 0;
    private readonly sectionOrder: SpartaxCQQuestionType[] = [
        SpartaxCQQuestionType.Familienstand,
        SpartaxCQQuestionType.Kinder,
        SpartaxCQQuestionType.Einkuenfte,
        SpartaxCQQuestionType.Behinderungen,
    ];

    public get familienstandQuestion() {
        return from(this.baseData.MasterQuestions).firstOrDefault(
            q => q.MasterQuestionType === SpartaxCQQuestionType.Familienstand);
    }

    public get kinderQuestion() {
        return from(this.baseData.MasterQuestions).firstOrDefault(
            q => q.MasterQuestionType === SpartaxCQQuestionType.Kinder);
    }

    public get einkuenfteQuestion() {
        return from(this.baseData.MasterQuestions).firstOrDefault(
            q => q.MasterQuestionType === SpartaxCQQuestionType.Einkuenfte);
    }

    public get behinderungenQuestion() {
        return from(this.baseData.MasterQuestions).firstOrDefault(
            q => q.MasterQuestionType === SpartaxCQQuestionType.Behinderungen);
    }

    public get anyMasterQuestionTrue(): boolean {
        if (this.model.cqMasterQuestionsAnswers) {
            for (const key in SpartaxCQQuestionType) {
                if (this.model.cqMasterQuestionsAnswers[key] === true) {
                    return true;
                }
            }
        } else {
            return false;
        }
    }

    public get yearsString(): string {
        let yearsOpen: number[] = [];
        let yearMax = moment().year() - 1;
        let yearMaxBaseOrder = Math.max(...this.baseData.ReferenceOrderYearsArray);
        
        for (let year = yearMax; year > yearMaxBaseOrder; year--) {
            yearsOpen.push(year);
        }
                
        if (yearsOpen.length === 1) {
            return `im Jahr ${yearsOpen[0]}`;
        } else if (yearsOpen.length) {
            const minYearOpen = Math.min(...yearsOpen); 
            const maxYearOpen = Math.max(...yearsOpen);
            return `in den Jahren ${minYearOpen} bis ${maxYearOpen}`;
        }
    }

    public getMasterQuestionAnswer(questionType: SpartaxCQQuestionType): boolean | null {
        if (!this.model.cqMasterQuestionsAnswers) {
            return null;
        }
        const value = this.model.cqMasterQuestionsAnswers[questionType];
        return typeof value === "undefined" ? null : value;
    }

    public getMasterQuestionAnswerString(questionType: SpartaxCQQuestionType): 'true' | 'false' | '' {
        const answer = this.getMasterQuestionAnswer(questionType);
        if (answer === true) {
            return 'true';
        } else if (answer === false) {
            return 'false';
        } else {
            return '';
        }
    }

    public setMasterQuestionAnswer(questionType: SpartaxCQQuestionType, value: boolean | null) {
        if (this.getMasterQuestionAnswer(questionType) === value) {
            value = null; // Toggle off if the same value is set again
        }
        if (!this.model.cqMasterQuestionsAnswers) {
            this.model.cqMasterQuestionsAnswers = {};
        }
        if (value === null) {
            delete this.model.cqMasterQuestionsAnswers[questionType];
        } else {
            this.model.cqMasterQuestionsAnswers[questionType] = value;
        }
        this.modelChange.emit(this.model);
    }

    public showPersonName(questionType: SpartaxCQQuestionType) {
        const question = from(this.baseData.MasterQuestions)
            .firstOrDefault(q => q.MasterQuestionType === questionType);

        switch (questionType) {
            case SpartaxCQQuestionType.Familienstand:
                return false;
            case SpartaxCQQuestionType.Kinder:
                return true;
            case SpartaxCQQuestionType.Einkuenfte:
            case SpartaxCQQuestionType.Behinderungen:
                return question?.Personen.length > 1;
            default:
                return true;
        }
    }

    public get availableSections(): SpartaxCQQuestionType[] {
        return this.sectionOrder.filter(type => {
            const question = this.getMasterQuestion(type);
            return question && question.Personen?.length > 0;
        });
    }

    public get hasPreviousSection(): boolean {
        // Button ist immer aktiv: entweder zurück zum vorherigen Slide oder zum vorherigen Stepper-Schritt
        return true;
    }

    public get hasNextSection(): boolean {
        // Button ist nur aktiv, wenn die aktuelle Section beantwortet wurde
        return this.isCurrentSectionAnswered();
    }

    private isCurrentSectionAnswered(): boolean {
        const sections = this.availableSections;
        if (sections.length === 0 || this.activeSectionIndex < 0 || this.activeSectionIndex >= sections.length) {
            return false;
        }
        const currentSectionType = sections[this.activeSectionIndex];
        return this.getMasterQuestionAnswer(currentSectionType) !== null;
    }

    public canNavigateToSection(targetSectionIndex: number): boolean {
        // Kann nur zum aktuellen oder zu bereits beantworteten Schritten navigieren
        // oder vorwärts nur wenn aktueller Schritt beantwortet wurde
        if (targetSectionIndex < this.activeSectionIndex) {
            // Rückwärts ist immer erlaubt
            return true;
        } else if (targetSectionIndex === this.activeSectionIndex) {
            // Zum aktuellen Schritt ist immer erlaubt
            return true;
        } else {
            // Vorwärts nur wenn aktueller Schritt beantwortet wurde
            return this.isCurrentSectionAnswered();
        }
    }

    public isSectionActive(questionType: SpartaxCQQuestionType): boolean {
        const activeSection = this.ensureActiveSectionIndex();
        return activeSection === questionType;
    }

    public goToPreviousSection(): void {
        if (this.activeSectionIndex === 0) {
            // Am Anfang der Slides -> Navigate zum vorherigen Stepper-Schritt
            this.navigateToPreviousStepper.emit();
        } else if (this.hasPreviousSection) {
            this.activeSectionIndex -= 1;
            this.scrollToSection();
        }
    }

    public goToNextSection(): void {
        // Nur weiterschalten wenn die aktuelle Section beantwortet wurde
        if (!this.isCurrentSectionAnswered()) {
            return;
        }

        const sections = this.availableSections;
        if (this.activeSectionIndex >= sections.length - 1) {
            // Am Ende der Slides -> Navigate zum nächsten Stepper-Schritt
            this.navigateToNextStepper.emit();
        } else {
            this.activeSectionIndex += 1;
            this.scrollToSection();
        }
    }

    override formIsValid(): boolean {
        return super.formIsValid() &&
            this.getMasterQuestionAnswer(SpartaxCQQuestionType.Familienstand) !== null &&
            this.getMasterQuestionAnswer(SpartaxCQQuestionType.Kinder) !== null &&
            this.getMasterQuestionAnswer(SpartaxCQQuestionType.Einkuenfte) !== null &&
            this.getMasterQuestionAnswer(SpartaxCQQuestionType.Behinderungen) !== null;
    }

    private getMasterQuestion(questionType: SpartaxCQQuestionType) {
        switch (questionType) {
            case SpartaxCQQuestionType.Familienstand:
                return this.familienstandQuestion;
            case SpartaxCQQuestionType.Kinder:
                return this.kinderQuestion;
            case SpartaxCQQuestionType.Einkuenfte:
                return this.einkuenfteQuestion;
            case SpartaxCQQuestionType.Behinderungen:
                return this.behinderungenQuestion;
            default:
                return null;
        }
    }

    private ensureActiveSectionIndex(): SpartaxCQQuestionType | null {
        const sections = this.availableSections;
        if (!sections.length) {
            return null;
        }

        if (this.activeSectionIndex < 0) {
            this.activeSectionIndex = 0;
        }

        if (this.activeSectionIndex > sections.length - 1) {
            this.activeSectionIndex = sections.length - 1;
        }

        return sections[this.activeSectionIndex];
    }

    private scrollToSection(): void {
        setTimeout(() => {
            const elementToScroll = document.querySelector('.mat-step:has(.beeex-spartax-master-question) .mat-step-header');
            if (elementToScroll) {
                elementToScroll.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 250);
    }
}
